import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Chip from '@mui/material/Chip';
import LinearProgress from '@mui/material/LinearProgress';
import Typography from '@mui/material/Typography';
import { makeStyles } from '@mui/styles';
import IconButton from 'components/IconButton/IconButton';
import { PauseIcon, PlayIcon } from 'components/icons';
import React, { useState, useEffect, useRef } from 'react';
import { formatDuration } from 'utils';

const useStyles = makeStyles(() => ({
  audio: {},
}));

const AudioPlayer = ({
  compact,
  fullWidth,
  hideDuration,
  autoPlay,
  url,
  initialDuration,
  onPlay: onPlayProp,
}) => {
  const [duration, setDuration] = useState(initialDuration ?? 0);
  const [progress, setProgress] = useState(0);
  const classes = useStyles();
  const audioRef = useRef();
  const [playing, setPlaying] = useState(false);

  useEffect(() => {
    setPlaying(false);
    setProgress(0);
  }, [url]);

  const togglePlay = () => {
    if (playing) {
      audioRef.current.pause();
      setPlaying(false);
    } else {
      audioRef.current.play();
    }
  };

  const onLoadMetadata = async () => {
    const dur = audioRef.current.duration;
    if (isFinite(dur)) {
      setDuration(dur);
    }
  };

  const onTimeUpdate = () => {
    const currentTime = audioRef.current.currentTime;
    const dur = audioRef.current.duration;
    setDuration(dur);
    setProgress((currentTime * 100) / dur);
  };

  const onPlay = () => {
    setPlaying(true);
    if (onPlayProp) onPlayProp();
  };

  const onEnded = () => {
    setPlaying(false);
    setProgress(0);
  };

  const audioComponent = (
    <audio
      onPlay={onPlay}
      autoPlay={autoPlay}
      onLoadedMetadata={onLoadMetadata}
      onTimeUpdate={onTimeUpdate}
      onEnded={onEnded}
      ref={audioRef}
      className={classes.audio}
      src={url}
      preload="true"
    />
  );

  if (!compact) {
    return (
      <Card
        variant="outlined"
        sx={{
          display: 'flex',
          alignItems: 'center',
          width: fullWidth ? '100%' : 240,
          px: 2,
          py: 1.5,
          bgcolor: 'background.paper4',
        }}
      >
        <Box>
          <IconButton
            variant="text"
            edge="start"
            size="small"
            onClick={togglePlay}
            icon={playing ? <PauseIcon /> : <PlayIcon />}
          />
        </Box>
        <LinearProgress
          variant="determinate"
          value={progress}
          sx={{
            width: '100%',
            mr: 1,
            '& .MuiLinearProgress-bar': { transition: 'transform .2s linear' },
          }}
        />
        {duration ? (
          <Typography variant="caption" sx={{ whiteSpace: 'nowrap' }}>
            {formatDuration(duration * 1000)}
          </Typography>
        ) : null}
        {audioComponent}
      </Card>
    );
  }
  if (hideDuration) {
    return (
      <>
        <IconButton
          variant="text"
          size="small"
          color="primary"
          onClick={togglePlay}
          icon={playing ? <PauseIcon /> : <PlayIcon />}
        />
        {audioComponent}
      </>
    );
  }
  return (
    <Box
      sx={{
        position: 'relative',
        bgcolor: 'primary.subtle',
        borderRadius: 30,
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          bgcolor: 'primary.highlight',
          top: 0,
          bottom: 0,
          width: `${progress}%`,
          opacity: 0.4,
        }}
      />
      <Chip
        clickable
        sx={{ bgcolor: 'transparent' }}
        onClick={togglePlay}
        size="mini"
        color="primary"
        variant="outlined"
        icon={playing ? <PauseIcon /> : <PlayIcon />}
        label={formatDuration(duration * 1000)}
      />
      {audioComponent}
    </Box>
  );
};

export default AudioPlayer;
